﻿import * as metastoreApi from "../../shared/metastore-api-client";
import * as coreApi from "../../shared/core-api-client";
import {
    AppError,
    AppUser,
    Banner,
    BreadcrumbItem,
    DISMISS_BANNER,
    LOAD_APP,
    SET_BREADCRUMB_STACK,
    SET_GLOBAL_ERROR,
    SET_MENU_IS_EXPANDED,
    SET_MENU_ITEMS,
    SET_PAGE_ERROR,
    SHOW_APP,
    SHOW_BANNER,
    TOGGLE_MENU
} from "./types";
import { initializeDatasourceFetchPipeline } from "../storyline/actions";
import { RootState } from "..";
import { ClientAppSettings } from "../../shared/providers/SettingsProvider";
import { loadPlugin } from "../../shared/services/pluginService";
import { refreshTenantThemeStylesheet, setActiveBranchCookie } from "../../shared/tenant-theme";


export function loadApp(user: AppUser, _settings: ClientAppSettings, _activeBranch: string) {
    return async (dispatch, getState: () => RootState) => {
        dispatch({
            type: LOAD_APP,
            user,
            settings: _settings,
        });

        initializeDatasourceFetchPipeline()(dispatch, getState);

        const lastActiveBranch = localStorage.getItem(`${user.name}::activeBranch`);
        const activeBranch = lastActiveBranch ? lastActiveBranch : _activeBranch;

        dispatch(loadAppData(_settings, activeBranch));
    }
}

export function switchBranch(activeBranch: string) {
    return async (dispatch, getState: () => RootState) => {
        const { user, settings } = getState().app;
        const storageKey = `${user.name}::activeBranch`;

        // Clear out previous errors...
        dispatch(setPageError(null));
        dispatch(setGlobalError(null));

        // Clear out previous banner...
        dispatch(dismissBanner());

        if (activeBranch) {
            localStorage.setItem(storageKey, activeBranch);
            dispatch(loadAppData(settings, activeBranch));
        } else {
            localStorage.removeItem(storageKey);
            dispatch(loadAppData(settings, settings.activeBranch));
        }
    }
}

function ensureMenuItemsHaveUniqueNames(items: metastoreApi.MenuItemModel[], parentName?: string) {
    const seenNames = new Set<string>();

    for (let [index, item] of items.entries())
    {
        // Ensure uniqueness across this level...
        if (seenNames.has(item.name))
            item.name = `${item.name}_${index}`;

        seenNames.add(item.name);

        // Prepend parent name to ensure uniqueness across child levels...
        if (parentName)
            item.name = `${parentName}::${item.name}`;

        // Ensure uniqueness across the child subtree...
        if (item.children) {
            ensureMenuItemsHaveUniqueNames(item.children, item.name);
        }
    }
}

function loadAppData(_settings: ClientAppSettings, branchName: string) {
    return async (dispatch, getState: () => RootState) => {
        const { coreApiUrl, domainName, metastoreUrl } = _settings;
        const settings = {
            ..._settings,
            ...(await new coreApi.SettingsClient(coreApiUrl).getClientSettings(domainName, branchName).catch(ex => {
                const getError = (ex): AppError => {
                    if (ex.status == null)
                        return { type: "Connectivity", message: `Unable to load settings from the Core API.`, details: `Unable to contact the server at \`${coreApiUrl}\`.<br />Please ensure you are connected to your corporate network or VPN.<br />If the issue persists, please contact the site administrator.` };

                    if (ex.status === 404)
                        return { type: "NotFound", message: "Tenant not found.", details: `No tenant is configured for this domain (\`${domainName}\`).<br />Please check the address and try again.` };

                    if (ex.status === 429)
                        return { type: "Connectivity", message: "Rate limit exceeded.", details: `Too many requests were submitted within the last minute.<br />Please wait a moment and try again.` };

                    return { type: "Generic", message: "An error occurred while loading settings from the Core API.", details: ex.response };
                };

                dispatch(setPageError(getError(ex)));

                return {};
            }))
        };

        // If the requested branch is not one returned by the Core API, the requested branch is no longer available.
        // Clear out the stale branch name from local storage (if present) and continue with the fallback branch returned...
        if (settings.currentBranch && settings.currentBranch !== branchName) {
            const { user } = getState().app;
            const storageKey = `${user.name}::activeBranch`;
            localStorage.removeItem(storageKey);
        }

        // Keep the tenant-theme branch cookie in sync with the resolved branch (cleared on the default
        // branch - an absent branchName means "default" to the Core API). Left untouched when settings
        // failed to load, to avoid clobbering good state on a transient error...
        if (settings.currentBranch) {
            const previousBranch = getState().app.activeBranch;
            setActiveBranchCookie(settings.currentBranch !== settings.activeBranch ? settings.currentBranch : null);

            // In-place branch switch (no page reload) - refetch the stylesheet so the new theme applies immediately...
            if (previousBranch && previousBranch !== settings.currentBranch)
                refreshTenantThemeStylesheet();
        }

        // Display the maintenance banner if the current branch is in maintenance mode...
        if (settings.maintenanceMode) {
            dispatch(setGlobalError({
                type: "Maintenance",
                message: settings.maintenanceMode.title || "This site is currently undergoing maintenance.",
                details: settings.maintenanceMode.body || "We apologize for the inconvenience - please try again later."
            }));

            dispatch({
                type: SHOW_APP,
                menuItems: [],
                settings,
                activeBranch: settings.currentBranch,
            });

            return;
        }

        if (settings.branchBanner) {
            dispatch(showBanner(settings.branchBanner as Banner));
        }

        await loadTenantPlugins(settings.plugins ?? []);

        dispatch({
            type: SHOW_APP,
            menuItems: "loading",
            settings,
            activeBranch: settings.currentBranch,
        });

        const menuItems = await
            metastoreApi.initializeGlobals(metastoreUrl) // Once-off initialization of the metastore version info, etc...
            .then(() => new metastoreApi.MenuItemsClient(metastoreUrl, settings.currentBranch).getAll())
            .catch(_ex => {
                // TODO: Temporary workaround for Debswana failing to load Menu Items concurrently...
                // const getError = (ex): AppError => {
                //     if (ex.status == null)
                //         return { type: "Connectivity", message: `Your browser cannot reach the Data Server.`, details: `Unable to contact the server at \`${metastoreUrl}\`.<br />Please ensure you are connected to your corporate network or VPN.<br />If the issue persists, please contact the site administrator.${ex["Q-Trace-Id"] ? "<br /><br /><strong>Trace ID:</strong> <code>" + ex["Q-Trace-Id"] : "</code>"}` };
        
                //     if (ex.status === 429)
                //         return { type: "Connectivity", message: "Rate limit exceeded.", details: `Too many requests were submitted within the last minute.<br />Please wait a moment and try again.` };
        
                //     return { type: "Generic", message: "Failed to load menu items.", details: `${ex.response}${ex["Q-Trace-Id"] ? "<br /><br /><strong>Trace ID:</strong> <code>" + ex["Q-Trace-Id"] : "</code>"}` };
                // };

                // dispatch(setPageError(getError(ex)));
                return [];
            });

        // Workaround for duplicate "name" fields within the Menu Item hierarchy...
        ensureMenuItemsHaveUniqueNames(menuItems);

        dispatch({
            type: SET_MENU_ITEMS,
            menuItems,
        });
    }
}

async function loadTenantPlugins(plugins: coreApi.PluginSettings[]) {
    // Add the linked CSS files to the document head...
    plugins.forEach(plugin => {
        const url = new URL(plugin.url);

        // If the plugin URL ends in the ".js" file extension, replace it with ".css" and attempt to load the style sheet...
        if (/\.js$/i.test(url.pathname)) {
            url.pathname = url.pathname.replace(/\.js$/i, ".css");

            document.head.appendChild(Object.assign(
                document.createElement("link"),
                {
                    rel: "stylesheet",
                    href: url.toString(),
                    crossorigin: "anonymous"
                }
            ));
        }
    });

    // Load the plugin modules and add them to the registry...
    await Promise.all(plugins.map(plugin => loadPlugin(plugin.url)));
}

export function toggleMenu() {
    return {
        type: TOGGLE_MENU
    }
}

export function setMenuIsExpanded(value: boolean) {
    return {
        type: SET_MENU_IS_EXPANDED,
        value: value
    }
}

export function setBreadcrumbStack(breadcrumbStack: BreadcrumbItem[]) {
    return {
        type: SET_BREADCRUMB_STACK,
        breadcrumbStack: breadcrumbStack
    }
}

export function setGlobalError(error: AppError) {
    return {
        type: SET_GLOBAL_ERROR,
        error
    };
}

export function setPageError(error: AppError) {
    return {
        type: SET_PAGE_ERROR,
        error
    };
}

export function showBanner(banner: Banner) {
    return {
        type: SHOW_BANNER,
        banner
    };
}

export function dismissBanner() {
    return {
        type: DISMISS_BANNER
    };
}